import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePickerComponentConfig } from '../../../../utils/date-picker/date-picker-component-config.interface';
import { cloneDeep } from 'lodash';
import { CrudViewColumnType, IconType } from '../../../../utils';
import { booleanValueOptions } from './utils/boolean-dropdown-options';
import { booleanConditions, dateConditions, numberConditions, stringConditions } from './utils/dropdown-items';
import { filterSupportedColumnTypes } from './utils/filter-supported-column-types';
import { FilterValue } from './utils/filter-value.interface';
import { CommonCondition, conditionMapping, DateCondition, NumberCondition, StringCondition } from './utils/operations';
@Component({
  selector: 'rappider-grid-filter',
  templateUrl: './grid-filter.component.html',
  styleUrls: ['./grid-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => GridFilterComponent),
      multi: true
    }
  ]
})
export class GridFilterComponent implements OnInit, ControlValueAccessor {

  @Input() columnType: CrudViewColumnType;
  @Input() data: any;
  @Input() fieldName: string;
  @Input() datePicker: DatePickerComponentConfig;

  @Output() dataChange = new EventEmitter<any>();

  filterSupportedColumnTypes = filterSupportedColumnTypes;
  CrudViewColumnType = CrudViewColumnType;

  _value: FilterValue;

  filterButtonConfig = {
    icon: {
      name: 'fa-solid fa-filter',
      type: IconType.FontAwesome
    }
  };

  removeFilterButtonConfig = {
    type: 'link',
    icon: {
      name: 'fa-solid fa-xmark',
      type: IconType.FontAwesome,
      color: 'var(--text-color)'
    }
  };

  booleanButtonConfig = {
    icon: {
      name: 'fa-solid fa-caret-down',
      type: IconType.FontAwesome
    }
  };

  filterIconConfig = {
    name: 'fa-solid fa-bars-filter'
  };

  OkButtonConfig = {
    text: 'Save',
    type: 'primary',
    size: 'small'
  };

  cancelButtonConfig = {
    text: 'Cancel',
    type: 'default',
    size: 'small'
  };

  dateConditions = dateConditions;
  numberConditions = numberConditions;
  stringConditions = stringConditions;
  booleanConditions = booleanConditions;

  booleanValueOptions = booleanValueOptions;

  defaultValue: FilterValue;

  dropdownVisibility = false;
  activeTab = true;
  isConditionPanelActive = true;
  isValuePanelActive = true;

  isFilterApplied = false;
  appliedFilterValue: FilterValue;

  get value() {
    return this._value;
  }

  set value(value: any) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  constructor() { }

  ngOnInit(): void {
    // Initialize default values if not already set
    if (!this._value) {
      this.initDefaultValue();
      this._value = cloneDeep(this.defaultValue);
    }
  }

  writeValue(value: FilterValue): void {
    this.initDefaultValue();

    if (value && value.filterValue !== null && value.filterValue !== undefined && value.filterValue !== '') {
      this._value = value;
      this.appliedFilterValue = cloneDeep(value);
      this.isFilterApplied = true;
    } else {
      this._value = cloneDeep(this.defaultValue);
      this.appliedFilterValue = null;
      this.isFilterApplied = false;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onConditionChange() {
    // Only apply filter if it's already been applied (saved)
    if (!this.isFilterApplied) {
      return;
    }

    const hasValidValue = this.value.filterValue !== null &&
      this.value.filterValue !== undefined &&
      this.value.filterValue !== '' ||
      [CommonCondition.IsEmpty, CommonCondition.IsNotEmpty].includes(this.value.condition);

    const conditionFunction = conditionMapping.find(item => item.type === this.columnType)?.filterFunction;
    if (conditionFunction && hasValidValue) {
      const filteredData = conditionFunction(this.data, this.value.filterValue, this.fieldName, this.value.condition);
      this.dataChange.emit(filteredData);
    } else {
      this.dataChange.emit(this.data);
    }
  }

  initDefaultValue() {
    if ([CrudViewColumnType.Text, CrudViewColumnType.Link, CrudViewColumnType.Tag, CrudViewColumnType.TagList,
    CrudViewColumnType.Icon, CrudViewColumnType.Image, CrudViewColumnType.ActionLink, CrudViewColumnType.Code,
    CrudViewColumnType.InputTemplate].includes(this.columnType)) {
      this.defaultValue = {
        condition: StringCondition.Contains,
        filterValue: null
      };
    } else if ([CrudViewColumnType.Number, CrudViewColumnType.Currency, CrudViewColumnType.Rate].includes(this.columnType)) {
      this.defaultValue = {
        condition: NumberCondition.IsEqualTo,
        filterValue: null
      };
    } else if ([CrudViewColumnType.Date, CrudViewColumnType.DateTime].includes(this.columnType)) {
      this.defaultValue = {
        condition: DateCondition.IsEqualTo,
        filterValue: null
      };
    } else if (this.columnType === CrudViewColumnType.Boolean) {
      this.defaultValue = {
        condition: CommonCondition.IsEqualTo,
        filterValue: null
      };
    } else {
      this.defaultValue = {
        condition: StringCondition.Contains,
        filterValue: null
      };
    }
  }

  isTypeSupportedForFilter() {
    return filterSupportedColumnTypes.some(item => item === this.columnType);
  }

  shouldShowValueRemoveIcon(): boolean {
    return this.value?.filterValue != null &&
      this.value.filterValue !== '' &&
      this.value.filterValue !== undefined;
  }

  // Remove only filter value
  removeFilterValue() {
    this.value.filterValue = null;

    // If filter was previously applied, update the applied state
    if (this.isFilterApplied) {
      this.isFilterApplied = false;
      this.appliedFilterValue = null;
      this.onChange(null);
    }
  }

  removeFilter() {
    this.initDefaultValue();
    this.value.condition = this.defaultValue.condition;
    this.value.filterValue = null;

    this.isFilterApplied = false;
    this.appliedFilterValue = null;

    // Emit null to parent component to indicate filter is removed
    this.onChange(null);

    this.dataChange.emit(this.data);

    this.isConditionPanelActive = true;
    this.isValuePanelActive = true;
    this.activeTab = true;
  }

  toggleDropdown(event) {
    this.dropdownVisibility = !this.dropdownVisibility;

    if (this.dropdownVisibility) {
      this.isConditionPanelActive = true;
      this.isValuePanelActive = true;
      this.activeTab = true;
      if (!this.isFilterApplied) {
        this.initDefaultValue();
        this.value = cloneDeep(this.defaultValue);
      }
    }

    event.stopPropagation();
  }

  changeActiveTabStatus() {
    this.activeTab = !this.activeTab;
  }

  applyFilter() {
    // Check if filter has a valid value
    const hasValidValue = this.value.filterValue !== null &&
      this.value.filterValue !== undefined &&
      this.value.filterValue !== '' ||
      [CommonCondition.IsEmpty, CommonCondition.IsNotEmpty].includes(this.value.condition);

    if (hasValidValue) {
      this.isFilterApplied = true;
      this.appliedFilterValue = cloneDeep(this.value);
      this.onChange(this.value);
    } else {
      this.isFilterApplied = false;
      this.appliedFilterValue = null;
      this.onChange(null);
    }

    const conditionFunction = conditionMapping.find(item => item.type === this.columnType)?.filterFunction;
    if (conditionFunction && hasValidValue) {
      const filteredData = conditionFunction(this.data, this.value.filterValue, this.fieldName, this.value.condition);
      this.dataChange.emit(filteredData);
    } else {
      this.dataChange.emit(this.data);
    }

    this.dropdownVisibility = false;
  }

  cancelFilter() {
    if (this.appliedFilterValue) {
      this.value = cloneDeep(this.appliedFilterValue);
    } else {
      this.initDefaultValue();
      this.value = cloneDeep(this.defaultValue);
    }

    // Don't emit to parent component when canceling
    this.dropdownVisibility = false;
  }
}
